import React, { useState, useEffect } from 'react';
import { useModalAnimation } from '../../hooks/useModalAnimation';
import { X, Save } from 'lucide-react';
import { GhostButton } from '../ui/GhostButton';
import { Input } from '../ui/Input';

export const AddressFormModal = ({ isOpen, onClose, address, onSave }) => {
    const { isVisible, isClosing, handleAnimationEnd } = useModalAnimation(isOpen, onClose);
    const [formData, setFormData] = useState({
        company_name: '',
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        street: '',
        number: '',
        address_line_2: '',
        postal_code: '',
        city: '',
        state: '',
        country: 'DE',
        type: 'General',
        notes: ''
    });

    useEffect(() => {
        if (address) {
            setFormData(address);
        } else {
            setFormData({
                company_name: '',
                first_name: '',
                last_name: '',
                email: '',
                phone: '',
                street: '',
                number: '',
                address_line_2: '',
                postal_code: '',
                city: '',
                state: '',
                country: 'DE',
                type: 'General',
                notes: ''
            });
        }
    }, [address, isOpen]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = () => {
        onSave(formData);
    };

    if (!isVisible) return null;

    return (
        <div
            className={`fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] ${isClosing ? 'animate-out fade-out duration-200' : 'animate-in fade-in duration-200'}`}
            onAnimationEnd={handleAnimationEnd}
        >
            <div className={`bg-stone-900 border border-stone-800 rounded-xl shadow-2xl w-[800px] max-h-[90vh] flex flex-col ${isClosing ? 'animate-out zoom-out-95 duration-200' : 'animate-in zoom-in-95 duration-200'}`}>
                {/* Header */}
                <div className="flex justify-between items-center p-6 border-b border-stone-800">
                    <h2 className="text-xl font-bold text-blue-400">
                        {address ? 'Edit Contact' : 'Create New Contact'}
                    </h2>
                    <button onClick={onClose} className="text-stone-400 hover:text-white transition">
                        <X size={20} />
                    </button>
                </div>

                {/* Body */}
                <div className="p-6 overflow-y-auto flex-1 space-y-4">
                    <div className="space-y-1">
                        <label className="text-xs font-bold text-stone-500">Company Name</label>
                        <Input name="company_name" value={formData.company_name} onChange={handleChange} placeholder="Company Inc." className="bg-stone-950 border-stone-700" />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1">
                            <label className="text-xs font-bold text-stone-500">First Name</label>
                            <Input name="first_name" value={formData.first_name} onChange={handleChange} className="bg-stone-950 border-stone-700" />
                        </div>
                        <div className="space-y-1">
                            <label className="text-xs font-bold text-stone-500">Last Name</label>
                            <Input name="last_name" value={formData.last_name} onChange={handleChange} className="bg-stone-950 border-stone-700" />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1">
                            <label className="text-xs font-bold text-stone-500">Email</label>
                            <Input name="email" value={formData.email} onChange={handleChange} className="bg-stone-950 border-stone-700" />
                        </div>
                        <div className="space-y-1">
                            <label className="text-xs font-bold text-stone-500">Phone</label>
                            <Input name="phone" value={formData.phone} onChange={handleChange} className="bg-stone-950 border-stone-700" />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1">
                            <label className="text-xs font-bold text-stone-500">Street</label>
                            <Input name="street" value={formData.street} onChange={handleChange} className="bg-stone-950 border-stone-700" />
                        </div>
                        <div className="space-y-1">
                            <label className="text-xs font-bold text-stone-500">Number</label>
                            <Input name="number" value={formData.number} onChange={handleChange} className="bg-stone-950 border-stone-700" />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1">
                            <label className="text-xs font-bold text-stone-500">Address Line 2</label>
                            <Input name="address_line_2" value={formData.address_line_2} onChange={handleChange} className="bg-stone-950 border-stone-700" />
                        </div>
                        <div className="space-y-1">
                            <label className="text-xs font-bold text-stone-500">Postal Code</label>
                            <Input name="postal_code" value={formData.postal_code} onChange={handleChange} className="bg-stone-950 border-stone-700" />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1">
                            <label className="text-xs font-bold text-stone-500">City</label>
                            <Input name="city" value={formData.city} onChange={handleChange} className="bg-stone-950 border-stone-700" />
                        </div>
                        <div className="space-y-1">
                            <label className="text-xs font-bold text-stone-500">State</label>
                            <Input name="state" value={formData.state} onChange={handleChange} className="bg-stone-950 border-stone-700" />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1">
                            <label className="text-xs font-bold text-stone-500">Country</label>
                            <select
                                name="country"
                                value={formData.country}
                                onChange={handleChange}
                                className="w-full bg-stone-950 border border-stone-700 rounded px-3 py-2 text-white focus:outline-none focus:border-blue-600"
                            >
                                <option value="DE">DE</option>
                                <option value="AT">AT</option>
                                <option value="CH">CH</option>
                                <option value="CN">CN</option>
                                <option value="US">US</option>
                            </select>
                        </div>
                        <div className="space-y-1">
                            <label className="text-xs font-bold text-stone-500">Type</label>
                            <select
                                name="type"
                                value={formData.type}
                                onChange={handleChange}
                                className="w-full bg-stone-950 border border-stone-700 rounded px-3 py-2 text-white focus:outline-none focus:border-blue-600"
                            >
                                <option value="General">General</option>
                                <option value="Supplier">Supplier</option>
                                <option value="Customer">Customer</option>
                                <option value="Main">Main</option>
                            </select>
                        </div>
                    </div>

                    <div className="space-y-1">
                        <label className="text-xs font-bold text-stone-500">Notes</label>
                        <textarea
                            name="notes"
                            value={formData.notes}
                            onChange={handleChange}
                            placeholder="Additional information..."
                            className="w-full bg-stone-950 border border-stone-700 rounded px-3 py-2 text-white focus:outline-none focus:border-blue-600 h-24 resize-none"
                        />
                    </div>
                </div>

                {/* Footer */}
                <div className="p-6 border-t border-stone-800 flex justify-end gap-3">
                    <GhostButton onClick={onClose}>Cancel</GhostButton>
                    <button
                        onClick={handleSubmit}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded font-medium transition flex items-center gap-2"
                    >
                        <Save size={16} /> Save
                    </button>
                </div>
            </div>
        </div>
    );
};
